import { Component, DestroyRef, inject } from '@angular/core';
import { MatNativeDateModule } from '@angular/material/core';
import { FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCalendar, faChartTreeMap, faPen, faUserPen } from '@fortawesome/pro-solid-svg-icons';
import { FieldMappingComponent, InstructionsComponent } from '@r1-compass-library/components';

import { Uuid } from '@r1-compass-library/utilities/uuid';

import { DefinedFieldSource, PicklistElement } from '@r1-compass-library/models';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';
import {
    AdditionalFieldsPicklistDialogData,
    PicklistDialogComponent,
} from './additional-fields-mapper-picklist-elements-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import {
    DisplayAssetDependencies,
    DisplayAssetDependenciesListComponent,
} from '@r1-compass-library/components/display-asset-dependencies-list/display-asset-dependencies-list.component';
import { Dependency } from '@r1-compass-library/models/dependencies.model';
import { DivisionBasicSettingsFormService } from '../division-basic-settings-form.service';
import { DivisionBasicSettingsFormValidatorsService } from '../division-basic-settings-form-validators.service';
import { DivisionBasicSettingsForm, PicklistForm } from '../form.model';
import { RowOptionButtons } from '@r1-compass-library/components/form-pieces/mapper/single-field.model';
import { HelperTextDialogComponent, HelperTextDialogData } from './helper-text-dialog.component';

@Component({
    selector: 'r1-compass-app-additional-fields-mapper',
    templateUrl: './additional-fields-mapper.component.html',
    styles: `
        .boolean-toggle {
            display: flex;
            justify-content: center;
            flex-direction: column;
            align-items: center;
            --mat-button-toggle-selected-state-background-color: var(--r1-brilliant-blue);
            --mat-button-toggle-selected-state-text-color: var(--r1-white);
        }
    `,
    imports: [
        FieldMappingComponent,
        FontAwesomeModule,
        InstructionsComponent,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        ReactiveFormsModule,
        MatButtonToggleModule,
        MatSlideToggleModule,
        MatDatepickerModule,
        MatNativeDateModule,
    ],
})
export class AdditionalFieldsMapperComponent {
    readonly #fb = inject(NonNullableFormBuilder);
    readonly #formService = inject(DivisionBasicSettingsFormService);
    readonly #basicSettingsValidators = inject(DivisionBasicSettingsFormValidatorsService);
    readonly #validators = inject(DivisionBasicSettingsFormValidatorsService);
    readonly #library = inject(FaIconLibrary);
    readonly #dialog = inject(MatDialog);
    readonly #destroy$ = inject(DestroyRef);

    constructor() {
        const library = this.#library;

        library.addIcons(faPen, faUserPen, faChartTreeMap);
    }

    protected readonly faCalendar = faCalendar;

    protected formArray: DivisionBasicSettingsForm['additionalFields'] =
        this.#formService.divisionBasicSettingsForm.controls.additionalFields;

    protected getDataTypeOptions(sourceType: 'User' | 'Static' | undefined) {
        const dataTypeOptions = [
            { value: 'varchar', display: 'Text' },
            { value: 'date', display: 'Date' },
            { value: 'picklist', display: 'Picklist' },
            { value: 'boolean', display: 'Boolean' },
        ] as const;

        if (sourceType === 'User') {
            return dataTypeOptions;
        } else if (sourceType === 'Static') {
            return dataTypeOptions.filter(option => option.value !== 'picklist');
        } else {
            return [];
        }
    }

    protected addMapperRow() {
        const rowId = Uuid.generate();
        this.formArray.push(
            this.#fb.group({
                id: this.#fb.control<string>(rowId),
                source: this.#fb.control<'Static' | 'User'>('Static'),
                name: this.#fb.control('', {
                    validators: [Validators.required, this.#basicSettingsValidators.nameTakenValidator(rowId)],
                }),
                type: this.#fb.control<'varchar' | 'boolean' | 'picklist' | 'date'>('varchar'),
                value: this.#fb.control<string | undefined>('', {
                    validators: this.#validators.additionFieldValueRequiredValidator(),
                }),
                required: this.#fb.control(false),
                picklistElements: this.#fb.array<FormGroup<PicklistForm>>([]),
                helperText: this.#fb.control(''),
            })
        );
        this.formArray.markAllAsDirty();
        this.#formService.divisionBasicSettingsForm.markAllAsDirty();
    }

    protected resetValueForm(index: number, type: string) {
        this.formArray.controls.at(index)?.controls.value.reset();
        this.formArray.controls.at(index)?.controls.picklistElements.clear();

        if (type === 'boolean') {
            this.formArray.controls.at(index)?.controls.value.updateValueAndValidity();
        }
    }

    protected setRowSourceType(type: DefinedFieldSource, index: number) {
        const fieldFormGroup = this.formArray.at(index);
        fieldFormGroup.controls.source.setValue(type);
        if (type === 'Static') {
            fieldFormGroup.controls.required.disable();
        } else {
            fieldFormGroup.controls.required.enable();
        }
        fieldFormGroup.controls.value.updateValueAndValidity();
    }

    protected onDateInput(event: MatDatepickerInputEvent<Date>, index: number) {
        const isoString = event.value?.toISOString();
        this.formArray.controls.at(index)?.controls.value.patchValue(isoString);
    }

    protected openPicklist(index: number, data: PicklistElement[], id: string) {
        this.#dialog
            .open<PicklistDialogComponent, AdditionalFieldsPicklistDialogData, number>(PicklistDialogComponent, {
                width: '600px',
                disableClose: true,
                data: {
                    index: index,
                    data: data,
                    formService: this.#formService,
                    additionalFieldId: id,
                },
            })
            .afterClosed()
            .pipe(takeUntilDestroyed(this.#destroy$))
            .subscribe(picklistData => {
                this.formArray.controls.at(picklistData ?? 0)?.controls.value.updateValueAndValidity();
            });
    }

    protected selectRowOption(index: number, id: string, value: RowOptionButtons['value']) {
        if (value === 'delete') {
            this.deleteFieldRow(index, id);
        } else if (value === 'addHelperText') {
            this.addHelperText(index);
        }
    }

    private addHelperText(index: number) {
        this.#dialog
            .open<HelperTextDialogComponent, HelperTextDialogData, string>(HelperTextDialogComponent, {
                width: '600px',
                disableClose: true,
                autoFocus: false,
                data: {
                    helperText: this.formArray.controls.at(index)?.controls.helperText.getRawValue() ?? '',
                },
            })
            .afterClosed()
            .subscribe(helperText => {
                // '' is valid if the user wants to remove the text
                this.#formService.addHelperText(helperText ?? '', index);
            });
    }

    private deleteFieldRow(index: number, id: string) {
        this.#formService.setAdditionalFieldDeletedForFieldMappingDeletion(id);

        this.#formService.getPlaysDependentOnAdditionalField(id).subscribe(dependentPlays => {
            const playsWithoutEmptyString = dependentPlays.filter(play => {
                return play.config?.definition?.additionalPlayFields?.find(field => field.id === id)?.value !== '';
            });

            if (dependentPlays.length > 0 && playsWithoutEmptyString.length === 0) {
                // If all the dependent plays have an empty string value,
                // then do not open the dialog, but still mark those fields for removal
                const playsWithAdditionalId = dependentPlays.map(play => ({
                    playId: play.id,
                    additionalFieldId: id,
                }));
                this.#formService.setAdditonalFieldsToDelete(playsWithAdditionalId);
                this.formArray.removeAt(index);
                this.formArray.markAllAsDirty();
                this.#formService.divisionBasicSettingsForm.markAllAsDirty();
            } else if (dependentPlays.length > 0) {
                // Otherwise, ask the user to confirm deletion.
                const playData: Dependency[] = playsWithoutEmptyString.map(play => ({
                    assetId: undefined,
                    playId: play.id,
                    name: play.name,
                }));
                this.#dialog
                    .open<DisplayAssetDependenciesListComponent, DisplayAssetDependencies, { deleteAll?: boolean }>(
                        DisplayAssetDependenciesListComponent,
                        {
                            width: '600px',
                            disableClose: true,
                            data: {
                                dependencies: playData,
                                dialogText: `This additional field is being used by ${playData.length} plays. It must be removed from each before deleting.`,
                                deleteAll: true,
                            },
                            autoFocus: false,
                        }
                    )
                    .afterClosed()
                    .subscribe(dependencyChoice => {
                        if (dependencyChoice?.deleteAll) {
                            const playsWithAdditionalId = dependentPlays.map(play => ({
                                playId: play.id,
                                additionalFieldId: id,
                            }));
                            this.#formService.setAdditonalFieldsToDelete(playsWithAdditionalId);

                            this.formArray.removeAt(index);
                            this.formArray.markAllAsDirty();
                            this.#formService.divisionBasicSettingsForm.markAllAsDirty();
                        }
                    });
            } else {
                this.formArray.removeAt(index);
                this.formArray.markAllAsDirty();
                this.#formService.divisionBasicSettingsForm.markAllAsDirty();
            }
        });
    }
}
